import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { IAnamneseCreateStatus, IAnamneseDetail, IAnamneseDetailPage } from '../models/anamnese.model';
import { IAnamneseQuestion } from '../models/anamnese-form-question.model';
import { IAnamneseAnswer } from '../models/anamnese-form-answer.model';
import { IAnamneseSendAnswers } from '../models/anamnese-sendanswers.model';
import { AnamneseSectionDto } from '../models/anamnese-section.model';
import { GlobalConfigService } from './global-config.service';

@Injectable({
  providedIn: 'root'
})
export class AnamneseService {
  private get baseUrl(): string {
    return this.globalConfig.getApiUrl() + '/documents/anamnese';
  }

  constructor(private http: HttpClient, private globalConfig: GlobalConfigService) { }

  listAnamneses(
    pageNumber: number,
    pageSize: number,
    orderBy: string,
    sortBy: string,
    patientId: string,
    moduleId: string,
    initialDate?: string,
    finalDate?: string,
  ): Observable<IAnamneseDetailPage> {
    return this.http.get<IAnamneseDetailPage>(`${this.baseUrl}`, {
      params: {
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString(),
        orderBy,
        sortBy,
        patientId,
        filterType: 'ALL',
        initial_date: initialDate || '2022-01-01',
        final_date: finalDate || new Date().toISOString().split('T')[0],
        module: moduleId || ''
      },
    });
  }

  listAnamnesesWithFilters(
    pageNumber: number,
    pageSize: number,
    orderBy: string,
    sortBy: string,
    patientId: string,
    filterType: 'COMPLETED' | 'NOT_COMPLETED' | 'ALL',
    initialDate: string,
    finalDate: string,
    moduleId: string
  ): Observable<IAnamneseDetailPage> {
    const params = {
      pageNumber,
      pageSize,
      orderBy,
      sortBy,
      patientId,
      filterType,
      initial_date: initialDate === '' ? '2022-01-01' : initialDate,
      final_date: finalDate === '' ? new Date().toISOString().split('T')[0] : finalDate,
      module: moduleId || ''
    };
  
    return this.http.get<IAnamneseDetailPage>(`${this.baseUrl}`, { params });
  }

  getAnamneseById(anamneseId: string): Promise<IAnamneseDetail> {
    return new Promise((resolve, reject) => {
      this.http.get<IAnamneseDetail>(`${this.baseUrl}/${anamneseId}`)
        .subscribe({
          next: (response) => {
            resolve(response);
          },
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }

  getAnamneseByIdPublic(anamneseId: string): Promise<IAnamneseDetail> {
    return new Promise((resolve, reject) => {
      this.http.get<IAnamneseDetail>(`${this.baseUrl}/public/${anamneseId}`)
        .subscribe({
          next: (response) => {
            resolve(response);
          },
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }

  getAnamneseCreateStatus(patientId: string): Observable<IAnamneseCreateStatus> {
    return this.http.get<IAnamneseCreateStatus>(`${this.baseUrl}/create/status?patientId=${patientId}`);
  }

  createAnamnese(anamnese: IAnamneseDetail): Observable<IAnamneseDetail> {
    return this.http.post<IAnamneseDetail>(`${this.baseUrl}`, anamnese);
  }

  deleteAnamnese(anamneseId: string): any {
    return this.http.delete<IAnamneseDetail>(`${this.baseUrl}/${anamneseId}`);
  }

  getQuestions(anamneseId: string): Promise<AnamneseSectionDto[]> {
    return new Promise((resolve, reject) => {
      this.http.get<AnamneseSectionDto[]>(`${this.baseUrl}/public/questions?anamneseId=${anamneseId}`)
        .subscribe({
          next: (response) => {
            resolve(response);
          },
          error: (err) => {
            reject(err?.error);
          }
        });
    });
  }
    
  sendAnswers(anamneseId: string, answers: IAnamneseSendAnswers): Promise<IAnamneseAnswer[]> {
    // Timeout curto (6s) — backend responde em <1s. Mobile com sinal ruim
    // costuma travar OPTIONS preflight intermitente; cortamos cedo e retentamos
    // automaticamente. Caso comum: 1ª retry resolve (~7.5s percebidos).
    // Worst case (3 tentativas todas timeoutam): ~20s antes de propagar erro.
    return new Promise((resolve, reject) => {
      this.http.post<IAnamneseAnswer[]>(`${this.baseUrl}/public/${anamneseId}/answers`, answers)
        .pipe(
          timeout(6000),
          retry({
            count: 2,
            delay: (error, retryCount) => {
              // Retenta APENAS em network error (status 0) ou TimeoutError do RxJS.
              // 4xx/5xx são problemas reais do backend — propaga sem retry.
              const isTransient =
                error?.status === 0 || error?.name === 'TimeoutError';
              if (!isTransient) {
                return throwError(() => error);
              }
              const backoffMs = retryCount === 1 ? 500 : 1500;
              return timer(backoffMs);
            }
          })
        )
        .subscribe({
          next: (response) => {
            resolve(response);
          },
          error: (err) => {
            reject(err?.error ?? err);
          }
        });
    });
  }
}
